import React, { useState, useEffect } from 'react';
import HiggsField from './components/HiggsField';
import OrbitalMap from './components/OrbitalMap';
import MassScale from './components/MassScale';
import ForcesSummary from './components/ForcesSummary';
import ParticleDetail from './components/ParticleDetail';
import ModeToggle from './components/ModeToggle';
import ModeComparisonModal from './components/ModeComparisonModal';
import CollisionSimulator from './components/CollisionSimulator';
import TimelineSlider from './components/TimelineSlider';
import HiggsDecayChain from './components/HiggsDecayChain';
import ProtonBuilder from './components/ProtonBuilder';
import StandardModelCracks from './components/StandardModelCracks';
import QuarkColorCharge from './components/QuarkColorCharge';
import WhatAmIMadeOf from './components/WhatAmIMadeOf';
import FeynmanBuilder from './components/FeynmanBuilder';
import ScaleNavigator from './components/ScaleNavigator';
import GuidedTour from './components/GuidedTour';
import ToroidalModel from './components/ToroidalModel';
import { PARTICLES, HIGGS, CATEGORIES } from './data';
import { RUACH_UI, RUACH_FACTS } from './ruach';
import './App.css';

const VIEWS = [
  'orbital','decay','timeline','collision','proton',
  'color','cracks','whatami','feynman','scale','mass','forces','toroidal'
];

// ─────────────────────────────────────────────────────────────────────────────
// © 2025 Stewards Under Pressure — Rooted Saviors Ministry
// A Stewards Under Pressure 508(c)(1)(A) Private Ministerial Association
// All rights reserved. Unauthorized reproduction or redistribution prohibited.
// Licensed exclusively for use at stewardsunderpressure.org and higgs-model.vercel.app
// Contact: stewardsunderpressure.org
// ─────────────────────────────────────────────────────────────────────────────

const ALLOWED_DOMAINS = [
  'stewardsunderpressure.org',
  'www.stewardsunderpressure.org',
  'higgs-model.vercel.app',
  'localhost',       // local development
  '127.0.0.1',       // local development
];

function isDomainAllowed() {
  const host = window.location.hostname;
  return ALLOWED_DOMAINS.some(d => host === d || host.endsWith('.' + d));
}

function DomainLocked() {
  return (
    <div style={{
      minHeight: '100vh', background: '#060810', color: '#fff',
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'center', padding: '2rem', textAlign: 'center',
      fontFamily: 'Space Mono, monospace',
    }}>
      <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>⚷</div>
      <div style={{ fontSize: '0.9rem', color: '#f0c040', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '1rem' }}>
        Access Restricted
      </div>
      <p style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.5)', maxWidth: '400px', lineHeight: 1.8, marginBottom: '1.5rem' }}>
        The Higgs Field Model is licensed exclusively for use at stewardsunderpressure.org.
        Embedding on unauthorized domains is not permitted.
      </p>
      <a href="https://stewardsunderpressure.org" style={{
        color: '#f0c040', fontSize: '0.75rem', letterSpacing: '0.08em',
        textDecoration: 'none', border: '1px solid rgba(240,192,64,0.4)',
        padding: '0.5rem 1.25rem', borderRadius: '4px',
      }}>
        Visit stewardsunderpressure.org →
      </a>
      <div style={{ marginTop: '2rem', fontSize: '0.65rem', color: 'rgba(255,255,255,0.2)' }}>
        © 2025 Stewards Under Pressure — Rooted Saviors Ministry
      </div>
    </div>
  );
}

export default function App() {
  const [view, setView] = useState('orbital');
  const [selected, setSelected] = useState(null);
  const [mode, setMode] = useState('science');
  const [showModal, setShowModal] = useState(false);
  const [showTour, setShowTour] = useState(false);
  const [introVisible, setIntroVisible] = useState(true);

  // Domain lock — only render on authorized domains
  if (!isDomainAllowed()) return <DomainLocked />;

  // Embed mode — ?embed=true strips sidebar and facts strip
  const isEmbed = new URLSearchParams(window.location.search).get('embed') === 'true';
  const isRuach = mode === 'ruach';
  const accentColor = isRuach ? '#ffb060' : '#f0c040';

  useEffect(() => {
    const t = setTimeout(() => setIntroVisible(false), isEmbed ? 0 : 4000);
    return () => clearTimeout(t);
  }, [isEmbed]);

  // Handle ?mode=ruach URL param
  useEffect(() => {
    const urlMode = new URLSearchParams(window.location.search).get('mode');
    if (urlMode === 'ruach') setMode('ruach');
  }, []);

  const viewLabels = {
    orbital: isRuach ? 'Breath & form' : 'Orbital',
    decay: isRuach ? 'Return' : 'Decay',
    timeline: isRuach ? 'תֹהוּ וָבֹהוּ' : 'Timeline',
    collision: isRuach ? 'Forms meet' : 'Collision',
    proton: isRuach ? 'Binding' : 'Proton',
    color: isRuach ? 'Three & one' : 'Color charge',
    cracks: isRuach ? 'Mystery' : 'SM cracks',
    whatami: 'What am I?',
    feynman: isRuach ? 'Vertex' : 'Feynman',
    scale: 'Scale',
    mass: isRuach ? 'Weight' : 'Mass',
    forces: isRuach ? 'Pillars' : 'Forces',
    toroidal: isRuach ? 'Field cycle' : 'Toroidal',
  };

  const viewHeaders = {
    orbital: { science: 'Higgs coupling — orbital model', ruach: 'Ruach Elohim — רוּחַ אֱלֹהִים', scienceSub: 'Particles orbit the Higgs at distances proportional to coupling strength. Toggle antimatter to reveal the mirror layer.', ruachSub: '"The Spirit of God was hovering over the waters." — Genesis 1:2. Toggle mirrors — מַרְאוֹת.' },
    decay: { science: 'Higgs boson decay chains', ruach: 'The Breath returns', scienceSub: 'Click any branch. H→γγ — massless photons from the mass-giving field — is the deepest paradox.', ruachSub: 'What does the Ruach return to? The γγ branch: pure light (אוֹר) emerging from the Breath itself.' },
    timeline: { science: 'Early universe timeline', ruach: 'From Tohu va-Bohu — תֹהוּ וָבֹהוּ', scienceSub: 'Drag backward. Watch the Higgs switch off, particles lose mass, forces unify.', ruachSub: 'Drag back to the void before creation. Genesis 1:2.' },
    collision: { science: 'Particle collision simulator', ruach: 'When forms meet', scienceSub: 'Select two particles and collide them.', ruachSub: 'When two formed things meet — what does the Ruach release?' },
    proton: { science: 'Proton builder', ruach: 'The first binding — הַקֶּשֶׁר הָרִאשׁוֹן', scienceSub: '99% of the proton\'s mass comes from QCD confinement — not the Higgs.', ruachSub: '"In him all things hold together." — Colossians 1:17.' },
    color: { science: 'Quark color charge (QCD)', ruach: 'Three become one', scienceSub: 'Only colorless quark combinations can exist as free particles.', ruachSub: '"The LORD our God, the LORD is one." — Deuteronomy 6:4.' },
    cracks: { science: 'Where the Standard Model breaks down', ruach: 'Where knowing ends — אֵין סוֹף', scienceSub: 'The Standard Model describes ~5% of the universe.', ruachSub: '"For now we see only a reflection as in a mirror." — 1 Corinthians 13:12.' },
    whatami: { science: 'What are you made of?', ruach: 'How much of you is Ruach-given weight?', scienceSub: 'Enter your weight and discover where your mass actually comes from.', ruachSub: '"I praise you because I am fearfully and wonderfully made." — Psalm 139:14' },
    feynman: { science: 'Feynman diagram builder', ruach: 'Build a vertex', scienceSub: 'Add particles, build an interaction, validate conservation laws.', ruachSub: 'Add particles to build a vertex — the moment of interaction.' },
    scale: { science: 'Scale navigator', ruach: 'From creation\'s expanse to the Planck void', scienceSub: 'Slide from the observable universe to the Planck length.', ruachSub: '"He stretches out the heavens like a canopy." — Isaiah 40:22' },
    mass: { science: 'Mass scale', ruach: 'Weight of being', scienceSub: 'Logarithmic comparison of all particle masses.', ruachSub: '"To each one the manifestation of the Spirit is given." — 1 Corinthians 12:7' },
    forces: { science: 'Four forces', ruach: 'Four pillars of creation', scienceSub: 'The Higgs controls the range of the weak force.', ruachSub: 'Creation operates through four fundamental interactions.' },
    toroidal: { science: 'Toroidal field model', ruach: 'The field cycle — הַמַּחְזוֹר', scienceSub: 'The toroidal pattern appears at every scale — atomic, cellular, cosmic. Matter gathers, pressure builds, energy releases, and returns.', ruachSub: 'The same pattern at every scale your Father made. Proton. Cell. Body. Earth. Galaxy. "He stretches out the heavens like a canopy." — Isaiah 40:22' },
  };

  const facts = isRuach ? RUACH_FACTS : [
    { label: 'Field type', value: 'Scalar (spin 0)' },
    { label: 'Discovered', value: 'CERN, 2012' },
    { label: 'Higgs mass', value: '125.1 GeV' },
    { label: 'SM describes', value: '~5% of universe' },
    { label: 'Dark matter', value: '~27%' },
    { label: 'Dark energy', value: '~68%' },
    { label: 'Stays massless', value: 'γ, g' },
    { label: 'You from QCD', value: '~99%' },
  ];

  const vh = viewHeaders[view] || viewHeaders.orbital;

  if (isEmbed) {
    return (
      <div style={{ minHeight: '100vh', background: '#060810', color: '#fff' }}>
        <HiggsField mode={mode} />
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.75rem 1rem', borderBottom: '1px solid rgba(255,255,255,0.07)', background: 'rgba(6,8,16,0.9)' }}>
            <div style={{ display: 'flex', gap: 0 }}>
              {['orbital','decay','timeline','mass'].map(v => (
                <button key={v} onClick={() => setView(v)} style={{ background: 'none', border: 'none', color: view === v ? accentColor : 'rgba(255,255,255,0.4)', fontFamily: 'Space Mono', fontSize: '0.68rem', padding: '0.5rem 0.75rem', cursor: 'pointer' }}>
                  {viewLabels[v]}
                </button>
              ))}
            </div>
            <ModeToggle mode={mode} onChange={setMode} />
          </div>
          <div style={{ padding: '1rem' }}>
            {view === 'orbital' && <OrbitalMap onSelect={setSelected} selected={selected} mode={mode} />}
            {view === 'decay' && <HiggsDecayChain mode={mode} />}
            {view === 'timeline' && <TimelineSlider mode={mode} />}
            {view === 'mass' && <MassScale onSelect={setSelected} selected={selected} mode={mode} />}
          </div>
        </div>
        {selected && <ParticleDetail particle={selected} onClose={() => setSelected(null)} mode={mode} />}
        <div style={{ textAlign: 'center', padding: '0.5rem', borderTop: '1px solid rgba(255,255,255,0.06)', background: 'rgba(6,8,16,0.95)' }}>
          <a href="https://stewardsunderpressure.org" target="_blank" rel="noopener noreferrer"
            style={{ fontFamily: 'Space Mono', fontSize: '0.58rem', color: accentColor, textDecoration: 'none', letterSpacing: '0.06em' }}>
            © 2025 Stewards Under Pressure — Rooted Saviors Ministry · stewardsunderpressure.org
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className={`app-root${isRuach ? ' ruach-mode' : ''}`}>
      <HiggsField mode={mode} />

      {introVisible && (
        <div className="intro-overlay" onClick={() => setIntroVisible(false)}>
          <div className="intro-content">
            <div className="intro-tag">Standard Model Explorer</div>
            <h1 className="intro-title">The Higgs Field</h1>
            <p className="intro-sub">How one field gives mass to the universe</p>
            <div className="intro-tap">tap to explore · SCIENCE / RUACH · רוּחַ</div>
          </div>
        </div>
      )}

      {showModal && <ModeComparisonModal onClose={() => setShowModal(false)} mode={mode} />}
      {showTour && <GuidedTour mode={mode} onNavigate={setView} onClose={() => setShowTour(false)} />}

      <div className="layout">
        {/* Sidebar */}
        <aside className="sidebar">
          <div className="logo">
            <span className="logo-H" style={{ background: accentColor }}>{isRuach ? 'א' : 'H'}</span>
            <div>
              <div className="logo-title">{isRuach ? RUACH_UI.appTitle : 'Higgs Model'}</div>
              <div className="logo-sub">{isRuach ? RUACH_UI.appSubtitle : 'Standard Model Explorer'}</div>
            </div>
          </div>

          <p className="sidebar-intro">
            {isRuach
              ? 'The Ruach Elohim — רוּחַ אֱלֹהִים — breathes form and weight into matter. That which receives the breath is formed. That which does not remains as light.'
              : 'The Higgs field permeates all of space. Particles that couple to it acquire mass. Those that don\'t remain massless and travel at the speed of light.'}
          </p>

          {/* Tour button */}
          <button onClick={() => setShowTour(true)} style={{
            display: 'flex', alignItems: 'center', gap: '0.5rem',
            background: `${accentColor}18`, border: `1px solid ${accentColor}45`,
            color: accentColor, borderRadius: '8px', padding: '0.6rem 0.875rem',
            cursor: 'pointer', fontSize: '0.72rem', fontFamily: 'Space Mono',
            letterSpacing: '0.05em', width: '100%', marginBottom: '2px',
          }}>
            <span>▷</span> {isRuach ? 'Begin the Ruach path' : 'Take the guided tour'}
          </button>

          {/* Info button */}
          <button onClick={() => setShowModal(true)} style={{
            display: 'flex', alignItems: 'center', gap: '0.5rem',
            background: `${accentColor}08`, border: `1px solid ${accentColor}25`,
            color: accentColor + 'cc', borderRadius: '8px', padding: '0.6rem 0.875rem',
            cursor: 'pointer', fontSize: '0.72rem', fontFamily: 'Space Mono',
            letterSpacing: '0.05em', width: '100%',
          }}>
            <span>⟐</span> What changes between modes?
          </button>

          <div className="legend-block">
            {Object.entries(CATEGORIES).map(([key, cat]) => (
              <div key={key} className="legend-row">
                <div className="legend-dot" style={{ background: cat.color }} />
                <span style={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.75rem', fontFamily: 'Space Mono' }}>{cat.label}</span>
              </div>
            ))}
          </div>

          <div className="particle-list">
            <div className="list-header">All particles — click to explore</div>
            {[HIGGS, ...PARTICLES].map(p => (
              <button key={p.id} className={`particle-btn${selected?.id === p.id ? ' active' : ''}`}
                onClick={() => setSelected(p)} style={{ '--cat-color': CATEGORIES[p.category]?.color || '#fff' }}>
                <span className="p-symbol">{p.symbol}</span>
                <span className="p-name">{p.name}</span>
                <span className="p-mass">{p.massLabel}</span>
              </button>
            ))}
          </div>
        </aside>

        {/* Main */}
        <main className="main-content">
          <div className="top-bar">
            <nav className="view-nav">
              {VIEWS.map(v => (
                <button key={v} className={`view-tab${view === v ? ' active' : ''}`}
                  onClick={() => setView(v)} style={{ '--accent': accentColor }}>
                  {viewLabels[v]}
                </button>
              ))}
            </nav>
            <div style={{ padding: '0 1rem', display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
              {/* Mobile tour button */}
              <button onClick={() => setShowTour(true)} className="mobile-tour-btn" style={{
                display: 'none', padding: '5px 10px', borderRadius: '6px',
                background: `${accentColor}18`, border: `1px solid ${accentColor}40`,
                color: accentColor, fontFamily: 'Space Mono', fontSize: '0.65rem', cursor: 'pointer',
              }}>▷ Tour</button>
              <ModeToggle mode={mode} onChange={setMode} />
            </div>
          </div>

          <div className="view-body">
            <div className="view-header">
              <h2>{isRuach ? vh.ruach : vh.science}</h2>
              <p>{isRuach ? vh.ruachSub : vh.scienceSub}</p>
            </div>

            {view === 'orbital'   && <div className="orbital-view"><div className="orbital-canvas-wrap"><OrbitalMap onSelect={setSelected} selected={selected} mode={mode} /></div><div className="orbital-hint">{isRuach ? 'Click any particle · toggle mirrors — מַרְאוֹת' : 'Click any particle · toggle antimatter'}</div></div>}
            {view === 'decay'     && <div className="scroll-view"><HiggsDecayChain mode={mode} /></div>}
            {view === 'timeline'  && <div className="scroll-view"><TimelineSlider mode={mode} /></div>}
            {view === 'collision' && <div className="scroll-view"><CollisionSimulator mode={mode} /></div>}
            {view === 'proton'    && <div className="scroll-view"><ProtonBuilder mode={mode} /></div>}
            {view === 'color'     && <div className="scroll-view"><QuarkColorCharge mode={mode} /></div>}
            {view === 'cracks'    && <div className="scroll-view"><StandardModelCracks mode={mode} /></div>}
            {view === 'whatami'   && <div className="scroll-view"><WhatAmIMadeOf mode={mode} /></div>}
            {view === 'feynman'   && <div className="scroll-view"><FeynmanBuilder mode={mode} /></div>}
            {view === 'scale'     && <div className="scroll-view"><ScaleNavigator mode={mode} /></div>}
            {view === 'mass'      && <div className="scroll-view"><MassScale onSelect={setSelected} selected={selected} mode={mode} /></div>}
            {view === 'forces'    && <div className="scroll-view"><ForcesSummary mode={mode} /></div>}
            {view === 'toroidal'  && <div style={{paddingBottom:'3rem'}}><ToroidalModel mode={mode} /></div>}
          </div>
        </main>

        {/* Facts strip */}
        <div className="facts-strip">
          <div className="facts-title" style={{ color: accentColor }}>
            {isRuach ? RUACH_UI.factTitle : 'Field facts'}
          </div>
          {facts.map(f => (
            <div key={f.label} className="fact-row">
              <span className="fact-label">{f.label}</span>
              <span className="fact-value">{f.value}</span>
            </div>
          ))}
          <div className="fact-note" style={{ borderLeftColor: accentColor + '60' }}>
            {isRuach
              ? '"Since the creation of the world God\'s invisible qualities have been clearly seen, being understood from what has been made." — Romans 1:20'
              : 'The Standard Model describes ~5% of the universe with extraordinary precision. The remaining 95% — dark matter, dark energy — is completely outside it.'}
          </div>
          {/* Embed link */}
          <div style={{ marginTop: '1rem', padding: '0.75rem', borderRadius: '6px', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
            <div style={{ fontSize: '0.6rem', fontFamily: 'Space Mono', textTransform: 'uppercase', letterSpacing: '0.1em', color: 'rgba(255,255,255,0.2)', marginBottom: '0.3rem' }}>Embed on your site</div>
            <div style={{ fontSize: '0.62rem', fontFamily: 'Space Mono', color: 'rgba(255,255,255,0.3)', wordBreak: 'break-all', lineHeight: 1.5 }}>
              ?embed=true&mode=ruach
            </div>
          </div>

          {/* Copyright notice */}
          <div style={{ marginTop: '1.5rem', paddingTop: '1rem', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
            <div style={{ fontSize: '0.58rem', fontFamily: 'Space Mono', color: 'rgba(255,255,255,0.2)', lineHeight: 1.7 }}>
              © 2025 Stewards Under Pressure
            </div>
            <div style={{ fontSize: '0.58rem', fontFamily: 'Space Mono', color: 'rgba(255,255,255,0.15)', lineHeight: 1.7 }}>
              Rooted Saviors Ministry
            </div>
            <div style={{ fontSize: '0.58rem', fontFamily: 'Space Mono', color: 'rgba(255,255,255,0.12)', lineHeight: 1.7 }}>
              508(c)(1)(A) Private Ministerial Association
            </div>
            <a href="https://stewardsunderpressure.org" target="_blank" rel="noopener noreferrer"
              style={{ fontSize: '0.58rem', fontFamily: 'Space Mono', color: accentColor + '80', textDecoration: 'none', display: 'block', marginTop: '0.3rem' }}>
              stewardsunderpressure.org
            </a>
            <div style={{ fontSize: '0.55rem', fontFamily: 'Space Mono', color: 'rgba(255,255,255,0.1)', lineHeight: 1.7, marginTop: '0.3rem' }}>
              All rights reserved. Unauthorized reproduction or redistribution of this application or its content is prohibited.
            </div>
          </div>
        </div>
      </div>

      {selected && <ParticleDetail particle={selected} onClose={() => setSelected(null)} mode={mode} />}
    </div>
  );
}
